import type { EntityId } from './ids.js';
import type { PrivacyClass } from './privacy.js';
import type { RecordEnvelope } from './records.js';

/**
 * Problems & Repair — the growth engine's diagnostic half.
 *
 * Life OS exists to help the owner grow by classifying limiters honestly and
 * separating what is within CONTROL, within INFLUENCE, or to be ACCEPTED — then
 * turning FALSE or FIXABLE limits into capability growth WITHOUT shame.
 *
 * A Problem is therefore not a task and not a journal entry. It is a diagnosed
 * blocker carrying: where it sits on the control spectrum, what kind of limit
 * it really is, a root-cause hypothesis, the chosen intervention, and the
 * smallest next step. Resolution links to the systems/targets that fixed it.
 */

/** Where the blocker sits relative to the owner's agency. */
export type ControlCategory = 'control' | 'influence' | 'accept';
export const CONTROL_CATEGORIES: readonly ControlCategory[] = ['control', 'influence', 'accept'] as const;

/**
 * The honest nature of the limit:
 *  - false   : not actually true (a belief/assumption) → reframe it away.
 *  - fixable : real but changeable → build capability to remove it.
 *  - real    : genuinely fixed for now → accept and route around it.
 */
export type LimiterType = 'false' | 'fixable' | 'real';
export const LIMITER_TYPES: readonly LimiterType[] = ['false', 'fixable', 'real'] as const;

export type ProblemStatus = 'identified' | 'investigating' | 'repairing' | 'resolved' | 'accepted';
export const PROBLEM_STATUSES: readonly ProblemStatus[] = [
  'identified',
  'investigating',
  'repairing',
  'resolved',
  'accepted',
] as const;

/** The neutral path forward implied by a limiter type. Never a verdict on the person. */
export type RepairPath = 'reframe' | 'repair' | 'accept';

export interface Problem extends RecordEnvelope {
  readonly kind: 'problem';
  readonly title: string;
  readonly controlCategory: ControlCategory;
  readonly limiterType: LimiterType;
  readonly status: ProblemStatus;
  /** Root-cause hypothesis (what is really going on). */
  readonly cause: string;
  /** The chosen intervention (how it gets reframed, repaired, or routed around). */
  readonly intervention: string;
  /** The smallest concrete next move. */
  readonly nextStep: string;
  /** Linked targets/projects/systems that address this problem (ids only). */
  readonly linkedIds: readonly EntityId[];
}

export interface ProblemInput {
  readonly title: string;
  readonly controlCategory?: ControlCategory;
  readonly limiterType?: LimiterType;
  readonly cause?: string;
  readonly intervention?: string;
  readonly nextStep?: string;
  readonly privacyClass?: PrivacyClass;
}

/** Editable diagnostic fields (the envelope + status are managed separately). */
export interface ProblemPatch {
  readonly controlCategory?: ControlCategory;
  readonly limiterType?: LimiterType;
  readonly cause?: string;
  readonly intervention?: string;
  readonly nextStep?: string;
}

/**
 * The neutral path a limiter implies. Pure and total: every limiter type maps
 * to exactly one path, so a problem can never read as a personal failing.
 */
export function recommendedPath(limiterType: LimiterType): RepairPath {
  switch (limiterType) {
    case 'false':
      return 'reframe';
    case 'fixable':
      return 'repair';
    case 'real':
      return 'accept';
  }
}

/** False and fixable limits are growth-actionable; real limits route to acceptance. */
export function isGrowthActionable(limiterType: LimiterType): boolean {
  return limiterType === 'false' || limiterType === 'fixable';
}

/** A problem is open while it is still being worked; resolved/accepted are terminal-calm. */
export function isProblemOpen(status: ProblemStatus): boolean {
  return status === 'identified' || status === 'investigating' || status === 'repairing';
}

export interface RepairSummary {
  readonly total: number;
  readonly open: number;
  readonly growthActionable: number;
  readonly byControl: Record<ControlCategory, number>;
}

/** Pure, neutral roll-up for the Problems screen header. Never a score. */
export function summarizeProblems(
  problems: readonly Pick<Problem, 'status' | 'limiterType' | 'controlCategory'>[],
): RepairSummary {
  const byControl: Record<ControlCategory, number> = { control: 0, influence: 0, accept: 0 };
  let open = 0;
  let growthActionable = 0;
  for (const p of problems) {
    byControl[p.controlCategory] += 1;
    if (isProblemOpen(p.status)) open += 1;
    if (isProblemOpen(p.status) && isGrowthActionable(p.limiterType)) growthActionable += 1;
  }
  return { total: problems.length, open, growthActionable, byControl };
}
